import {
  Component,
  EventEmitter, inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { Record } from 'src/app/models/record';
import { ApiService } from 'src/app/services/api.service';
import { Rating, UserVote } from 'src/app/models/rating';
import { AuthService } from 'src/app/services/auth.service';
import { LocaleService } from 'src/app/services/locale.service';
import { Util } from 'src/app/util';
import { ToastService } from '../../_core/services/toast.service';

@Component({
  selector: 'app-rating',
  templateUrl: './rating.component.html',
  styleUrls: ['./rating.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RatingComponent implements OnInit, OnChanges {
  private readonly toastService = inject(ToastService);

  @Input()
  record: Record;

  @Input()
  recordId: string;

  @Input()
  readOnly: boolean;

  @Input()
  rating: Rating;

  @Input()
  mean: number;

  @Input()
  userRating: number;

  @Input()
  preventRating: boolean;

  @Output()
  rated: EventEmitter<Rating> = new EventEmitter();

  recordRating: Rating;
  displayRating: number;
  byUser: boolean;
  userRate: number;
  textDirection: string;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private localeService: LocaleService
  ) { }

  ngOnInit() {
    this.textDirection = this.localeService.getTextDirection();
    if (this.mean && !this.userRating) {
      this.displayRating = this.mean;
    } else if (this.userRating) {
      this.displayRating = this.userRating;
      this.byUser = true;
    } else if (this.rating) {
      this.recordRating = this.rating;
      this.setRatings(this.rating);
    } else if (this.record && !this.recordId) {
      this.getRating();
    } else {
      this.recordRating = Util.getEmptyRating();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.record && !changes.record.isFirstChange()) {
      this.getRating();
    } else if (changes.rating && !changes.rating.isFirstChange()) {
      this.recordRating = changes.rating.currentValue;
      this.setRatings(changes.rating.currentValue);
    } else if (changes.mean && !changes.mean.isFirstChange()) {
      this.displayRating = changes.mean.currentValue;
    }
  }

  getRating() {
    let userId: string;

    if (this.authService.isAuthenticated()) {
      userId = this.authService.getLoggedUser().id;
    }

    this.apiService.getRecordRating(this.record.id, this.record.isbn, userId).subscribe((rating) => {
      this.recordRating = rating;
      this.setRatings(rating);
    });
  }

  onRateChange(rate: number) {
    if (this.userRate === rate) {
      return;
    }

    this.userRate = rate;
    this.displayRating = rate;
    this.byUser = true;
    const userId = this.authService.getLoggedUser().id;
    const recordId = (this.record && this.record.id) || this.recordId;
    const isbn = this.record.isbn;

    this.apiService.rateRecord(recordId, userId, rate, isbn).subscribe((rating) => {
      this.recordRating = rating;
      this.toastService.showToast('messages.rating.rate');
      this.rated.emit(rating);
    });
  }

  setRatings(rating: Rating) {
    let userVote: UserVote;

    if (this.authService.isAuthenticated()) {
      const userId = this.authService.getLoggedUser().id;
      userVote = rating ? rating.userIdAndVoteList.find((v) => v.userId === userId) : null;
    }

    if (userVote) {
      this.displayRating = userVote.vote;
      this.byUser = true;
      this.userRate = userVote.vote;
    } else {
      this.displayRating = rating ? rating.mean : 0;
      this.byUser = false;
    }
  }

  setPreventRatingAndRate(value: boolean) {
    this.preventRating = value;
    this.onRateChange(this.userRate);
  }
}
